import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { rutaAccionCliente } from '@/lib/ruta-cliente'
import { useGlobalSearch, type SearchResult } from '../hooks/useGlobalSearch'
import {
  Search,
  User,
  FileText,
  CheckSquare,
  Calendar,
  Loader2,
  Plus,
  Wallet,
  CreditCard,
  Briefcase,
  MoreHorizontal,
  Eye,
  Pencil,
  Receipt,
  Building2,
  CircleHelp,
  Megaphone,
} from 'lucide-react'

const TIPO_ICONOS: Record<string, React.ElementType> = {
  clientes: User,
  movimientos: Wallet,
  tareas: CheckSquare,
  cuentas: CreditCard,
  servicios: Briefcase,
  presupuestos: FileText,
  clientes_v2: User,
  // Legacy
  personas: User,
  documentos: FileText,
  vencimientos_fiscales: Calendar,
}

// Acciones rápidas para el estado vacío
type AccionRapida = {
  id: string
  label: string
  icon: React.ElementType
  ruta?: string
}

const ACCIONES_RAPIDAS: AccionRapida[] = [
  { id: 'nuevo-cliente', label: 'Nuevo Cliente', icon: User },
  { id: 'nuevo-movimiento', label: 'Nuevo Movimiento', ruta: '/movimientos?nuevo=true', icon: Wallet },
  { id: 'nueva-tarea', label: 'Nueva Tarea', ruta: '/tareas?nuevo=true', icon: CheckSquare },
  { id: 'nuevo-presupuesto', label: 'Nuevo Presupuesto', ruta: '/presupuestos?nuevo=true', icon: FileText },
]

const NAVEGACION_RAPIDA = [
  { label: 'Clientes', ruta: '/clientes', icon: User },
  { label: 'Movimientos', ruta: '/movimientos', icon: Wallet },
  { label: 'Cuentas', ruta: '/cuentas', icon: CreditCard },
  { label: 'Servicios', ruta: '/servicios', icon: Briefcase },
  { label: 'Presupuestos', ruta: '/presupuestos', icon: FileText },
  { label: 'Tareas', ruta: '/tareas', icon: CheckSquare },
  { label: 'Ayuda', ruta: '/ayuda', icon: CircleHelp },
  { label: 'Novedades de Menta', ruta: '/novedades', icon: Megaphone },
]

interface GlobalSearchProps {
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  // Callbacks para acciones rápidas
  onNuevoCliente?: () => void
  // Callbacks para resultados de clientes
  onClienteVerDetalle?: (clienteId: string) => void
  onClienteEditar?: (clienteId: string) => void
  onClienteEstadoCuenta?: (clienteId: string) => void
}

export function GlobalSearch({
  isOpen: controlledIsOpen,
  onOpenChange,
  onNuevoCliente,
  onClienteVerDetalle,
  onClienteEditar,
  onClienteEstadoCuenta,
}: GlobalSearchProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const navigate = useNavigate()

  const isOpen = controlledIsOpen ?? internalIsOpen
  const setIsOpen = onOpenChange ?? setInternalIsOpen

  const {
    query,
    setQuery,
    resultadosAgrupados,
    isLoading,
    limpiar,
    obtenerRuta,
  } = useGlobalSearch({ debounceMs: 300, limit: 30 })

  // Abrir con Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setIsOpen])

  // Limpiar cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      limpiar()
    }
  }, [isOpen, limpiar])

  const handleSelect = useCallback((ruta: string) => {
    setIsOpen(false)
    navigate(ruta)
  }, [setIsOpen, navigate])

  const handleSelectResult = useCallback((resultado: SearchResult) => {
    // Para clientes, usar el callback de ver detalle si está disponible
    if (resultado.entidad_tipo === 'clientes' && onClienteVerDetalle) {
      setIsOpen(false)
      // Pequeño delay para permitir que el dialog se cierre antes de abrir el Sheet
      setTimeout(() => {
        onClienteVerDetalle(resultado.entidad_id)
      }, 150)
      return
    }
    const ruta = obtenerRuta(resultado)
    handleSelect(ruta)
  }, [obtenerRuta, handleSelect, onClienteVerDetalle, setIsOpen])

  // Handler para acciones rápidas
  const handleAccionRapida = useCallback((accion: AccionRapida) => {
    setIsOpen(false)
    if (accion.id === 'nuevo-cliente' && onNuevoCliente) {
      // Pequeño delay para permitir que el dialog se cierre antes de abrir el Sheet
      setTimeout(() => {
        onNuevoCliente()
      }, 150)
    } else if (accion.ruta) {
      navigate(accion.ruta)
    }
  }, [setIsOpen, navigate, onNuevoCliente])

  // Handlers para acciones de cliente
  const handleClienteEditar = useCallback((clienteId: string) => {
    setIsOpen(false)
    if (onClienteEditar) {
      onClienteEditar(clienteId)
    } else {
      navigate(rutaAccionCliente(clienteId, 'editar'))
    }
  }, [setIsOpen, navigate, onClienteEditar])

  const handleClienteEstadoCuenta = useCallback((clienteId: string) => {
    setIsOpen(false)
    if (onClienteEstadoCuenta) {
      onClienteEstadoCuenta(clienteId)
    } else {
      navigate(rutaAccionCliente(clienteId, 'estado-cuenta'))
    }
  }, [setIsOpen, navigate, onClienteEstadoCuenta])

  const getIcon = (tipo: string) => {
    return TIPO_ICONOS[tipo] || FileText
  }

  // Parsear información del cliente desde el resultado
  const parseClienteInfo = (resultado: SearchResult) => {
    // contenido tiene formato: "XX-XXXXXXXX-X · CLI-XXX"
    // descripcion tiene formato: "Tipo Persona · Condición Fiscal"
    const contenido = resultado.contenido || ''
    const descripcion = resultado.descripcion || ''

    const [cuit, codigo] = contenido.split(' · ')
    const [tipoPersona, condicionFiscal] = descripcion.split(' · ')

    return {
      cuit: cuit || '',
      codigo: codigo || '',
      tipoPersona: tipoPersona || '',
      condicionFiscal: condicionFiscal || '',
    }
  }

  const hasQuery = query.trim().length > 0
  const hasResults = resultadosAgrupados.length > 0

  return (
    <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
      <CommandInput
        placeholder="Buscar clientes, movimientos, tareas..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Empty state cuando hay query pero no resultados */}
        {hasQuery && !hasResults && !isLoading && (
          <CommandEmpty>
            No se encontraron resultados para "{query}"
          </CommandEmpty>
        )}

        {/* Resultados de búsqueda */}
        {hasQuery && hasResults && !isLoading && (
          <>
            {resultadosAgrupados.map((grupo) => {
              return (
                <CommandGroup key={grupo.tipo} heading={grupo.label}>
                  {grupo.resultados.map((resultado) => {
                    const ResultIcon = getIcon(resultado.entidad_tipo)
                    const isCliente = resultado.entidad_tipo === 'clientes'

                    // Renderizado especial para clientes
                    if (isCliente) {
                      const clienteInfo = parseClienteInfo(resultado)
                      return (
                        <CommandItem
                          key={`${resultado.entidad_tipo}-${resultado.entidad_id}`}
                          value={`${resultado.titulo} ${resultado.contenido || ''}`}
                          onSelect={() => handleSelectResult(resultado)}
                          className="flex items-center gap-3 py-3 group"
                        >
                          <div className="p-1.5 rounded bg-muted">
                            {clienteInfo.tipoPersona.includes('Jurídica') ? (
                              <Building2 className="h-4 w-4" />
                            ) : (
                              <User className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{resultado.titulo}</p>
                              {clienteInfo.tipoPersona && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                  {clienteInfo.tipoPersona.includes('Jurídica') ? 'PJ' : 'PF'}
                                </Badge>
                              )}
                              {clienteInfo.condicionFiscal && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                  {clienteInfo.condicionFiscal.includes('Inscripto') ? 'RI' :
                                   clienteInfo.condicionFiscal.includes('Monotribut') ? 'Mono' :
                                   clienteInfo.condicionFiscal.includes('Exento') ? 'Ex' :
                                   clienteInfo.condicionFiscal.includes('Final') ? 'CF' : ''}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {clienteInfo.cuit} · {clienteInfo.codigo}
                            </p>
                          </div>

                          {/* Menú de acciones */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem onClick={() => handleSelectResult(resultado)}>
                                <Eye className="mr-2 h-4 w-4" />
                                Ver detalle
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleClienteEditar(resultado.entidad_id)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleClienteEstadoCuenta(resultado.entidad_id)}>
                                <Receipt className="mr-2 h-4 w-4" />
                                Estado de cuenta
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </CommandItem>
                      )
                    }

                    // Renderizado genérico para otros tipos
                    return (
                      <CommandItem
                        key={`${resultado.entidad_tipo}-${resultado.entidad_id}`}
                        value={`${resultado.titulo} ${resultado.contenido || ''}`}
                        onSelect={() => handleSelectResult(resultado)}
                        className="flex items-center gap-3 py-3"
                      >
                        <div className="p-1.5 rounded bg-muted">
                          <ResultIcon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{resultado.titulo}</p>
                          {resultado.contenido && (
                            <p className="text-xs text-muted-foreground truncate">
                              {resultado.contenido}
                            </p>
                          )}
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )
            })}
          </>
        )}

        {/* Estado inicial - Acciones y navegación rápida */}
        {!hasQuery && !isLoading && (
          <>
            <CommandGroup heading="Acciones rápidas">
              {ACCIONES_RAPIDAS.map((accion) => {
                const Icon = accion.icon
                return (
                  <CommandItem
                    key={accion.id}
                    value={accion.label}
                    onSelect={() => handleAccionRapida(accion)}
                    className="flex items-center gap-3 py-2.5"
                  >
                    <div className="p-1.5 rounded bg-primary/10 text-primary">
                      <Plus className="h-4 w-4" />
                    </div>
                    <span className="flex-1">{accion.label}</span>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </CommandItem>
                )
              })}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Ir a">
              {NAVEGACION_RAPIDA.map((item) => {
                const Icon = item.icon
                return (
                  <CommandItem
                    key={item.ruta}
                    value={item.label}
                    onSelect={() => handleSelect(item.ruta)}
                    className="flex items-center gap-3 py-2"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{item.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}

// Botón trigger para el Header
export function GlobalSearchTrigger({ onClick }: { onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 h-9 px-3 text-sm text-muted-foreground bg-muted/50 hover:bg-muted border rounded-md transition-colors"
      aria-label="Buscar (⌘K)"
    >
      <Search className="h-4 w-4" />
      <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
        <span className="text-xs">⌘</span>K
      </kbd>
    </button>
  )
}
